import NamedRegExp from 'named-regexp-groups'
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'
import PizZip from 'pizzip'
import { ToastContainer, toast } from 'react-toastify' //EXPORT BOTH OF THESE
import { BatchModeTransaction, BatchOperationAdd, IRListItem } from '@rhybus/api'
import { convertToRaw } from 'draft-js'
import readXlsxFile from 'read-excel-file'
import Swal from 'sweetalert2'
import Tesseract from 'tesseract.js'

import { mpfRListsHttp } from '../api/mpf-r-lists-http'
import { PIIAuditTrail, COLUMN_NAMES as PIIAuditTrailCols } from '../data/pii/pii-audit-trail'

interface File {
  lastModified: number
  lastModifiedDate: Date
  name: string
  size: number
  type: string
  webkitRelativePath: string
}
interface PIIScanInfo {
  canSubmit: boolean
  piiScanResult: any
}
type FileExtension =
  | 'docx'
  | 'gif'
  | 'jpeg'
  | 'jpg'
  | 'pdf'
  | 'png'
  | 'tif'
  | 'tiff'
  | 'txt'
  | 'xlsx'
// <<-------------------------- Constants -------------------------->>

const FILE_ARRAY = 'FileArray'
const ERROR_ALERT_TYPE = 'swal'
const LABELS_TO_SCAN = [
  'Question',
  'Title',
  'Subject',
  'Description',
  'Answer',
  'Comment',
  'Message',
  'FileArray',
]
const LABELS_TO_SCAN_OBJ = {
  Answer: 'Answer',
  Comment: 'Comment',
  Description: 'Description',
  FileArray: 'File Uploaded',
  Message: 'Message',
  Question: 'Question',
  Subject: 'Subject',
  Title: 'Title',
}
const ERROR_MESSAGES = {
  AccessDeniedError: 'Something went wrong with Access denined',
  BatchError: 'Something went wrong with batch issue',
  ConcurrencyError: 'Something went wrong with concurrency issue',
  DuplicateError: 'Something went wrong with duplicate issue',
  GenericError: 'Something went wrong .Please contact administrator.',
  InvalidOperation: 'Something went wrong With Invalid Operation',
  NotFoundError: 'Something went wrong with not found issue',
  RhybusError: 'Something went wrong With Server Call',
  SchemaViolation: 'Something went wrong with schema violation issue',
}

//<<-------------------------- Generic Helper Functions  -------------------------->>
function getCurrentUTCDate() {
  return new Date().toISOString()
}

const getData = async (listName: string, filterVal) => {
  const data = await (await mpfRListsHttp().getList(listName)).query().filter(filterVal).get()
  return data
}

const PII: any = {}
let _pdfWorker: any = null
const gRefreshRequired = false
const uniqueFormatStartsWithAlpha = 'i'
const BATCH_SIZE = 1000

const AUDIT_LIST = 'PIIAuditTrailList'
const CATEGORY_PARAMETER_FORMATS_LIST = 'PIICategoryParameterFormatsList'
const CATEGORY_TYPE_PARAMETER_LIST = 'PIICategoryTypeParametersList'

//<<-------------------------------------- PII Audit Trail Related -------------------------------------->>
//Function to check and add to the PII Audit Trail
const shouldAddToPIIAuditTrail = async (piiScan): Promise<boolean | undefined> => {
  let foundResultsArray = false
  const files = piiScan.FileArray

  if (piiScan && Object.keys(piiScan).length > 0) {
    if (files && Array.isArray(files.value)) {
      for (const file of files.value) {
        if (file.resultsArray && Array.isArray(file.resultsArray) && file.resultsArray.length > 0) {
          foundResultsArray = true
          break
        }
      }
    }

    for (const key in piiScan) {
      if (
        Object.prototype.hasOwnProperty.call(piiScan, key) &&
        key !== 'FileArray' &&
        piiScan[key].resultsArray?.length > 0
      ) {
        foundResultsArray = true
        break
      }
    }

    return foundResultsArray
  }
}

/**addToPIIAuditTrail
 * Adds information to the PII Audit Trail.
 *
 * This function performs batch operations to add information to the PII (Personally Identifiable Information)
 * audit trail. It processes both labels and files associated with the PII scan, handling them in manageable
 * chunks defined by the batch size. The function ensures efficient and safe handling of user data.
 *
 * @param {string} componentName - The name of the component, which will be stored in the comment.
 * @param {string} itemGUID - The GUID (Globally Unique Identifier) or eKey.
 * @param {any} piiScan - The object containing the PII scan data.
 * @param {string} inquiryID - The ID of the inquiry or UserId.
 * @returns {Promise<void>} - A promise that resolves when the operation is complete.
 */
const addToPIIAuditTrail = async (
  componentName,
  itemGUID: string,
  piiScan: any,
  inquiryID: string
): Promise<void> => {
  const userID = await (await mpfRListsHttp().me()).userId
  const labels = LABELS_TO_SCAN.filter((label) => label in piiScan)
  const totalLabels = labels.length
  let fileBatch = 0
  let fileBatchSize = 0

  if (Array.isArray(piiScan.FileArray?.value)) {
    fileBatch = piiScan.FileArray.value.filter(
      (item) => Array.isArray(item.resultsArray) && item.resultsArray.length > 0
    ).length
    fileBatchSize = Math.min(fileBatch, BATCH_SIZE)
  }

  const totalSize = totalLabels + fileBatchSize

  try {
    for (let index = 0; index < totalSize; index += BATCH_SIZE) {
      await preformBatch(
        BatchOperationAdd,
        AUDIT_LIST,
        piiScan,
        componentName,
        userID,
        itemGUID,
        inquiryID,
        labels
      )
    }
  } catch (error) {
    console.error('PII', error)
    // ShowErrorMessage(ReturnErrorMessage(error.name));
  }
}

function ShowErrorMessage(message) {
  switch (ERROR_ALERT_TYPE) {
    case 'toast': {
      toast.error(message, {
        autoClose: false,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: 'top-center',
        progress: undefined,
        theme: 'colored',
      })
      break
    }
    case 'swal': {
      // if the api has signaled that a refresh is required,
      // there is no point in showing an oops message.
      // instead of showing a dialog, refresh to home.
      if (gRefreshRequired) {
        window.location.reload()
        break
      }

      Swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        icon: 'error',
        text: message,
        title: 'Oops...',
      })
      break
    }
    default:
      window.alert(message)
  }
}
/***** Popup Responsive Code End**** */
function ReturnErrorMessage(errorName) {
  switch (errorName) {
    case 'RhybusError':
      return ERROR_MESSAGES.RhybusError.toString()
    case 'InvalidOperation':
      return ERROR_MESSAGES.InvalidOperation.toString()
    case 'ConcurrencyError':
      return ERROR_MESSAGES.ConcurrencyError.toString()
    case 'DuplicateError':
      return ERROR_MESSAGES.DuplicateError.toString()
    case 'NotFoundError':
      return ERROR_MESSAGES.NotFoundError.toString()
    case 'BatchError':
      return ERROR_MESSAGES.BatchError.toString()
    case 'SchemaViolation':
      return ERROR_MESSAGES.SchemaViolation.toString()
    case 'AccessDeniedError':
      return ERROR_MESSAGES.AccessDeniedError.toString()
    default:
      return ERROR_MESSAGES.GenericError.toString()
  }
}

/**generatePIIAuditJson
 * Generates the PII Audit Trail batch to be used when adding items to the PII Audit Trail list
 * @param {object} piiScanItemObj pii scan results
 * @returns {object} type of PII data with the label = type and occurrences = how many PII were located
 */
function generatePIIAuditJson(piiScanItemObj): string {
  const returnObj = {
    groupByParam: {},
  }
  if (piiScanItemObj && piiScanItemObj.findingsAggregationObj) {
    Object.keys(piiScanItemObj.findingsAggregationObj).forEach(function (formatKey) {
      const aggObj = piiScanItemObj.findingsAggregationObj[formatKey]
      if (aggObj && Object.prototype.hasOwnProperty.call(aggObj, 'count') && aggObj.formatText) {
        returnObj.groupByParam[formatKey] = {
          label: aggObj.formatText,
          occurrences: aggObj.count,
        }
      }
    })
  }
  return JSON.stringify(returnObj)
}

// <<--------------------------------------------------- PII Scan <<--------------------------------------------------->>

// <<-------------------------- scanObject_v2 -------------------------->>

function scanObject_v2(objectToScan): Promise<unknown> {
  return new Promise(function (resolve: any, reject) {
    if (
      !(
        typeof objectToScan === 'object' &&
        objectToScan !== null &&
        Object.keys(objectToScan).length > 0
      )
    ) {
      return resolve({ error: 'Invalid Object' })
    }
    // construct pii metadata
    const piiScan: any = createPIIConfigTemplate()
    fillUniqueFormats(piiScan).then(function () {
      if (
        !(
          piiScan.uniqueFormatObj &&
          Array.isArray(piiScan.uniqueFormatObj.uniqueFormatsArray) &&
          piiScan.uniqueFormatObj.uniqueFormatsArray.length > 0
        )
      ) {
        return resolve({ error: 'No PII configuration found in your site' })
      }
      const objToReturn = {}
      Promise.all(
        Object.keys(objectToScan).map(function (attrKey) {
          return new Promise(function (resolveAttr: any, rejectAttr) {
            const attrVal = objectToScan[attrKey]
            if (typeof attrVal === 'string' || attrVal instanceof String) {
              const resultsObj = getPIIResultsArrayFromText_v2(
                piiScan.uniqueFormatObj,
                attrVal,
                attrKey
              )
              objToReturn[attrKey] = Object.assign({ value: attrVal }, resultsObj)
              return resolveAttr()
            } else if (typeof attrVal === 'number') {
              const resultsObj = getPIIResultsArrayFromText_v2(
                piiScan.uniqueFormatObj,
                '' + attrVal,
                attrKey
              )
              objToReturn[attrKey] = Object.assign({ value: attrVal }, resultsObj)
              return resolveAttr()
            } else if (attrVal instanceof File) {
              getPIIResultsArrayFromFile_v2(piiScan.uniqueFormatObj, attrVal).then(function (
                resultsObj
              ) {
                objToReturn[attrKey] = Object.assign({ value: attrVal }, resultsObj)
              })
              return resolveAttr()
            } else if (Array.isArray(attrVal)) {
              const allAttrValsArray: any = []
              Promise.all(
                attrVal.map(function (arrayItem) {
                  return new Promise(function (resolveArrayItem: any, rejectArrayItem) {
                    if (arrayItem instanceof File) {
                      getPIIResultsArrayFromFile_v2(piiScan.uniqueFormatObj, arrayItem).then(
                        function (resultsObj) {
                          allAttrValsArray.push(Object.assign({ value: arrayItem }, resultsObj))
                          return resolveArrayItem()
                        }
                      )
                    } else if (arrayItem && arrayItem.docName && arrayItem.docPath) {
                      getFormatOrParams(arrayItem.docPath, null)
                        .then(function (arrayBuffer) {
                          return extractTextFromFileBuffer(
                            arrayBuffer,
                            arrayItem.docName.split('.').pop()
                          )
                        })
                        .then(function (extractedText) {
                          return getPIIResultsArrayFromText_v2(
                            piiScan.uniqueFormatObj,
                            extractedText,
                            arrayItem.docName
                          )
                        })
                        .then(function (resultsObj) {
                          allAttrValsArray.push(Object.assign({ value: arrayItem }, resultsObj))
                          return resolveArrayItem()
                        })
                    } else {
                      allAttrValsArray.push({
                        value: arrayItem,
                      })
                      return resolveArrayItem()
                    }
                  })
                })
              ).then(function () {
                objToReturn[attrKey] = {
                  value: allAttrValsArray,
                }
                return resolveAttr()
              })
            } else {
              objToReturn[attrKey] = {
                value: attrVal,
              }
              return resolveAttr()
            }
          })
        })
      ).then(function () {
        return resolve(objToReturn)
      })
    })
  })
}
function createPIIConfigTemplate() {
  //Changed from getPIIConfigTemplate to createPIIConfigTemplate

  return {
    CategoryParams: [],
    CategoryParamsObj: {},
    CreatedTime: new Date().toISOString(),
    ExtraData: { groupByParam: {} },
    FoldersArray: [],
    ListsArray: [],
    RecordsDetected: 0,
    RecordsOccurrencesDetected: 0,
    RecordsScanned: 0,
    listsMetadata: {},
    resultsArray: [],
    scanId: null,
    status: 'STARTED',
  }
}
function fillUniqueFormats(piiScan) {
  return getUniqueFormats().then(function (uniqueFormatObj) {
    piiScan.uniqueFormatObj = uniqueFormatObj
    return piiScan
  })
}
function getUniqueFormats() {
  return new Promise(function (resolve, reject) {
    getFormatOrParams('PIICategoryParameterFormatsList', null)
      .then(function (categoryFormatsArray) {
        return getFormatOrParams('PIICategoryTypeParametersList', null).then(function (
          categoryParamsArray
        ) {
          return {
            categoryFormatsArray: categoryFormatsArray,
            categoryParamsArray: categoryParamsArray,
          }
        })
      })
      .then(function (dataObj) {
        if (
          !(
            dataObj &&
            Array.isArray(dataObj.categoryFormatsArray) &&
            Array.isArray(dataObj.categoryParamsArray)
          )
        ) {
          return resolve({})
        }
        const categoryParamsArray = dataObj?.categoryParamsArray
        const categoryFormatsArray = dataObj?.categoryFormatsArray
        const uniqueFormatsArray: {
          contextString: string
          formatString: string
          formats: any
        }[] = []
        const uniqueFormatStrings: string[] = []
        const categoryParamsObj = categoryParamsArray.reduce(function (acc, paramObj) {
          acc[paramObj.ID.toString()] = paramObj
          return acc
        }, {})
        // sort formats, because we will compare the regex string to check if the cache is valid or not
        categoryFormatsArray.sort(function (formatA, formatB) {
          if (formatA.ID < formatB.ID) {
            return -1
          } else if (formatA.ID > formatB.ID) {
            return 1
          }
          return 0
        })
        for (const format of categoryFormatsArray) {
          if (!(format && format.FormatRegExp)) {
            continue
          }
          if (!(format.PID && categoryParamsObj[format.PID.toString()])) {
            continue
          }
          // fill param in format
          format.param = categoryParamsObj[format.PID.toString()]

          if (format.FormatEnable === 'No' || format.param.ParamEnable === 'No') {
            continue
          }
          // check if this format string already exists
          const ufsIndex = uniqueFormatStrings.indexOf(format.FormatRegExp)

          if (ufsIndex === -1) {
            uniqueFormatStrings.push(format.FormatRegExp)
            uniqueFormatsArray.push({
              contextString:
                format.param && format.param.CategoryContext ? format.param.CategoryContext : '',
              formatString: format.FormatRegExp,
              formats: [format],
            })
          } else {
            const uniqueFormatItem = uniqueFormatsArray[ufsIndex]
            uniqueFormatItem.formats.push(format)

            if (format.param && format.param.CategoryContext) {
              if (uniqueFormatItem.contextString) {
                uniqueFormatItem.contextString += ','
              }
              uniqueFormatItem.contextString += format.param.CategoryContext
            }
          }
        }

        const groupedRegExString = uniqueFormatsArray
          .map(function (uniqueFormatItem, uniqueFormatItemIndex) {
            // using named groups
            return (
              '(?<' +
              uniqueFormatStartsWithAlpha +
              uniqueFormatItemIndex +
              '>' +
              uniqueFormatItem.formatString +
              ')'
            )
          })
          .join('|')

        const groupedRegEx = groupedRegExString ? new NamedRegExp(groupedRegExString, 'g') : null
        return resolve({
          groupedRegEx: groupedRegEx,
          groupedRegExString: groupedRegExString,
          uniqueFormatStrings: uniqueFormatStrings,
          uniqueFormatsArray: uniqueFormatsArray,
        })
      })
      .catch(function (err) {
        window.debugPii &&
          console.error(
            'An error occurred while constructing unique formats: ',
            JSON.stringify(err, Object.getOwnPropertyNames(err))
          )
        return resolve([])
      })
  })
}

async function getFormatOrParams(
  listName: 'PIICategoryParameterFormatsList' | 'PIICategoryTypeParametersList',
  defaultValue?
): Promise<Iterable<IRListItem>> {
  try {
    const data = await getData(listName, '')
    return data
  } catch (err) {
    if (window.debugPii) {
      alert(
        'Exception in getDataAndHandleError: ' +
          JSON.stringify(err, Object.getOwnPropertyNames(err))
      )
    }
    return defaultValue !== undefined ? defaultValue : null
  }
}
// <<-------------------------- scanObject_v2 -------------------------->>

//<-------------------------- discoverPII_v2 -------------------------->>
//#region Evaluations
function discoverPII_v2(
  regEx: any,
  val
): {
  displayText: string
  findingsAggregationObj: any
  findingsAggregationString: string
  resultsArray: []
} {
  if (Object.prototype.hasOwnProperty.call(regEx, 'lastIndex')) {
    regEx.lastIndex = 0
  }
  if (regEx.regex) {
    regEx.regex.lastIndex = 0
  }
  val = /<\/?[a-z][\s\S]*>/i.test(val) ? formatEnhancedRichText(val) : val //to format the results
  let counter = 0
  let regExResponse = null
  let allFormatsFoundText = val
  const findingsArray = []
  while (null != (regExResponse = regEx.exec(val))) {
    const indexStart = regExResponse.index // response will have start index
    const indexEnd = regEx.regex ? regEx.regex.lastIndex : regEx.lastIndex // and regEx will store the last index from which it has to scan again
    // eslint-disable-next-line no-loop-func
    const formatGroupIds = regExResponse.groups
      ? Object.keys(regExResponse.groups).filter(function (groupKey) {
          return groupKey[0] === uniqueFormatStartsWithAlpha && regExResponse.groups[groupKey]
        })
      : []
    findingsArray.push({
      formatGroupIds: formatGroupIds,
      indexEnd: indexEnd,
      indexStart: indexStart,
    })
    counter++
  }
  // create allFormatsFoundText
  if (counter > 0) {
    // reset
    regEx.lastIndex = 0
    allFormatsFoundText = allFormatsFoundText
      .toString()
      .replace(regEx, '<span style="background-color:yellow">$&</span>')
  }

  return {
    allFormatsFoundText: allFormatsFoundText,
    count: counter,
    findingsArray: findingsArray,
  }
}
function formatEnhancedRichText(comment: string): string {
  if (comment !== undefined || comment != null) {
    comment = comment.replace(/\n/g, '<br />')
  }
  return comment
}
//<-------------------------- discoverPII_v2 -------------------------->>

// <<--------------------------------------------------- Get Files --------------------------------------------------->>

// <<-------------------------- getPIIResultsArrayFromText_v2 -------------------------->>

// helper function for scanObject_v2
function getPIIResultsArrayFromText_v2(
  uniqueFormatObj,
  extractedText,
): {
  displayText: string
  findingsAggregationObj: any
  findingsAggregationString: string
  resultsArray: never[]
} {
  const resultsArray = []
  const findingsObj = discoverPII_v2(
    uniqueFormatObj.groupedRegEx,
    extractedText
  )
  if (
    findingsObj &&
    Array.isArray(findingsObj.findingsArray) &&
    findingsObj.findingsArray.length > 0
  ) {
    for (const element of findingsObj.findingsArray) {
      const findingPart = element
      const finding = {
        EndIndex: findingPart.indexEnd,
        StartIndex: findingPart.indexStart,
      }
      // fill formats -> these are formats passed for each finding
      const formatIndexes = findingPart.formatGroupIds.map(function (groupKey) {
        return Number(groupKey.split(uniqueFormatStartsWithAlpha)[1])
      })

      finding.formats = formatIndexes
        .reduce(function (formatsArray, uniqueFormatIndex) {
          const uniqueFormat = uniqueFormatObj.uniqueFormatsArray[uniqueFormatIndex]
          formatsArray = formatsArray.concat(uniqueFormat.formats)
          return formatsArray
        }, [])
        .sort(function (formatObjA, formatObjB) {
          if (formatObjA.ID < formatObjB.ID || formatObjA.ID > formatObjB.ID) {
            return -1
          }
          return 0
        })

      finding.FormatIds = finding.formats
        .map(function (format) {
          return format.ID
        })
        .sort()

      resultsArray.push(finding)
    }
  }
  // create all findings aggregation string by param
  let findingsAggString = ''
  const findingsAggObj = {}
  for (const element of resultsArray) {
    const finding = element
    const allFindingParamsObj = finding.formats
      .map(function (formatObj) {
        return formatObj.param
      })
      .reduce(function (acc, param) {
        // to get unique params
        acc[param.ID.toString()] = param
        return acc
      }, {})

    const uniqueParamIdString = Object.keys(allFindingParamsObj)
      .map((paramId) => Number(paramId))
      .sort((a, b) => a - b)
      .join('_')
    if (!findingsAggObj[uniqueParamIdString]) {
      findingsAggObj[uniqueParamIdString] = {
        count: 0,
        formatText: uniqueParamIdString
          .split('_')
          .map(function (paramId) {
            return allFindingParamsObj[paramId].CategoryParamName
          })
          .join('/'),
      }
    }
    findingsAggObj[uniqueParamIdString].count++
  }

  findingsAggString = getFindingsAggregationString(findingsAggObj)
  return {
    displayText: findingsObj.allFormatsFoundText,
    findingsAggregationObj: findingsAggObj,
    findingsAggregationString: findingsAggString,
    resultsArray: resultsArray,
  }
}
function getFindingsAggregationString(findingsAggregationObj): string {
  if (findingsAggregationObj && Object.keys(findingsAggregationObj).length > 0) {
    return Object.keys(findingsAggregationObj)
      .sort()
      .map(function (key) {
        return findingsAggregationObj[key].formatText + ': ' + findingsAggregationObj[key].count
      })
      .join(', ')
  }
  return ''
}

// <<-------------------------- getPIIResultsArrayFromText_v2 -------------------------->>

// helper function for scanObject_v2
function getPIIResultsArrayFromFile_v2(uniqueFormatObj, fileInstance) {
  return new Promise(function (resolve, reject) {
    if (!(fileInstance instanceof File)) {
      return resolve([])
    }
    getArrayBufferFromFile(fileInstance)
      .then(function () {
        return extractTextFromFileBuffer(
          fileInstance.arrayBuffer,
          fileInstance.name.split('.').pop(),
          fileInstance
        )
      })
      .then(function (extractedText) {
        return getPIIResultsArrayFromText_v2(uniqueFormatObj, extractedText, '')
      })
      .then(resolve)
  })
}
function getArrayBufferFromFile(fileInstance) {
  return new Promise(function (resolve, reject) {
    const fileReader = new FileReader()
    fileReader.readAsArrayBuffer(fileInstance)

    fileReader.onload = function () {
      fileInstance.arrayBuffer = fileReader.result
      return resolve(fileInstance)
    }
    fileReader.onerror = function () {
      window.debugPii &&
        alert(
          'Error reading File: ' + fileInstance.name,
          JSON.stringify(fileReader.error, Object.getOwnPropertyNames(fileReader.error))
        )
      return resolve(fileInstance)
    }
  })
}
function getMSExcel(arrayBuffer) {
  const excelPromises = []
  const fullExcelData = []
  return new Promise(function (resolve, reject) {
    if (arrayBuffer) {
      readXlsxFile(arrayBuffer, { getSheets: true })
        .then(function (sheets) {
          for (const sheet of sheets) {
            excelPromises.push(
              extractTextFromMSExcel(arrayBuffer, sheet.name).then(function (data) {
                fullExcelData.push(data)
              })
            )
          }
          Promise.all(excelPromises).then(function () {
            resolve(fullExcelData.join(' '))
          })
        })
        .catch(function (err) {
          resolve('')
        })
    } else {
      resolve('')
    }
  })
}
function getPDFWorker() {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker
  if (!_pdfWorker) {
    _pdfWorker = new pdfjsLib.PDFWorker('pii')
  }
  return _pdfWorker
}

//<<-------------------------- Extractor Functions -------------------------->>
function extractTextFromFileBuffer(
  fileArrayBuffer,
  fileExtension: FileExtension,
  fileInstance?: any
) {
  return new Promise(function (resolve, reject) {
    if (!fileArrayBuffer || !fileExtension) {
      return resolve('')
    }

    if (fileExtension === 'docx') {
      extractTextFromMSWord(fileInstance).then(resolve)
    } else if (fileExtension === 'xlsx') {
      getMSExcel(fileArrayBuffer).then(resolve)
    } else if (fileExtension === 'pdf') {
      extractTextFromPDF(fileArrayBuffer).then(resolve)
    } else if (fileExtension.match(/(jpg|jpeg|png|gif|tif|tiff)$/i)) {
      extractTextFromImage(fileArrayBuffer).then(resolve)
    } else if (fileExtension === 'txt') {
      readBinaryStringFromArrayBuffer(fileArrayBuffer).then(resolve)
    } else {
      return resolve('')
    }
  })
}
function extractTextFromPDF(arrayBuffer) {
  return new Promise(function (resolve, reject) {
    if (arrayBuffer) {
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        worker: getPDFWorker(),
      })
      loadingTask.promise
        .then(function (pdf) {
          const maxPages = pdf.numPages
          const countPromises = [] // collecting all page promises
          for (let j = 1; j <= maxPages; j++) {
            const page = pdf.getPage(j)
            countPromises.push(
              page.then(function (page) {
                // add page promise
                const textContent = page.getTextContent()
                return textContent.then(function (text) {
                  // return content promise
                  return text.items
                    .map(function (s) {
                      return s.str
                    })
                    .join(' ')
                })
              })
            )
          }

          // Wait for all pages and join text
          Promise.all(countPromises).then(function (texts) {
            loadingTask.destroy()
            resolve(texts.join(' '))
          })
        })
        .catch(function (err) {
          window.debugPii &&
            console.error(
              'Exception in extractTextFromPDF',
              JSON.stringify(err, Object.getOwnPropertyNames(err))
            )
          loadingTask.destroy()
          resolve('')
        })
    } else {
      resolve('')
    }
  })
}
function extractTextFromImage(imagePathOrArrayBuffer) {
  return new Promise(function (resolve, reject) {
    if (imagePathOrArrayBuffer) {
      Tesseract.recognize(new Blob([new Uint8Array(imagePathOrArrayBuffer).buffer]))
        .then(function (tessResponse) {
          return resolve(tessResponse && tessResponse.data.text ? tessResponse.data.text : '')
        })
        .catch(function (err) {
          window.debugPii &&
            console.error(
              'Exception in extractTextFromImage',
              JSON.stringify(err, Object.getOwnPropertyNames(err))
            )
          resolve('')
        })
    } else {
      resolve('')
    }
  })
}
function extractTextFromMSExcel(arrayBuffer, name: string) {
  return new Promise(function (resolve, reject) {
    // `rows` is an array of rows
    // each row being an array of cells.
    readXlsxFile(arrayBuffer, { sheet: name })
      .then(function (rows) {
        window.debugPii && console.error(rows)
        resolve(JSON.stringify(rows))
      })
      .catch(function (err) {
        window.debugPii &&
          console.error(
            'Exception in extractTextFromExcel',
            JSON.stringify(err, Object.getOwnPropertyNames(err))
          )
        resolve('')
      })
  })
}
async function extractTextFromMSWord(fileInstance) {
  try {
    const extractTextFromWordDoc = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          const arrayBuffer = event.target.result
          const zip = new PizZip(arrayBuffer)

          try {
            const xmlData = zip.file('word/document.xml').asText()
            const extractedText = parseXMLAndSetText(xmlData)
            resolve(extractedText) // Return the extracted text
          } catch (error) {
            console.error('Error reading document:', error)
            reject(error) // Handle errors
          }
        }

        reader.onerror = (error) => {
          reject(error) // Handle errors from FileReader
        }

        reader.readAsArrayBuffer(file)
      })
    }

    const parseXMLAndSetText = (xmlData) => {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlData, 'application/xml')

      // Traverse the XML and extract text from <w:t> tags
      const textElements = xmlDoc.getElementsByTagName('w:t')
      let text = ''

      for (let i = 0; i < textElements.length; i++) {
        text += textElements[i].textContent + ' '
      }

      return text.trim() // Return the extracted text
    }
    const text = await extractTextFromWordDoc(fileInstance)
    return text
  } catch (error) {
    window.debugPii &&
      console.error(
        'Exception in extractTextFromMSWord',
        JSON.stringify(error, Object.getOwnPropertyNames(error))
      )
    return ''
  }
}

// converting to file instance even in case of file upload, have to refactor
function readBinaryStringFromArrayBuffer(arrayBuffer) {
  return new Promise(function (resolve, reject) {
    const reader = new FileReader()
    reader.onload = function (event) {
      if (!event) {
        return resolve(reader.content)
      }
      return resolve(event.target.result)
    }
    reader.onerror = function (event) {
      console.error(
        'An error occurred while reading from text file',
        JSON.stringify(err, Object.getOwnPropertyNames(event.target.error))
      )
      return resolve('')
    }
    reader.readAsBinaryString(new Blob([arrayBuffer], { type: 'application/octet-stream' }))
  })
}
// <<--------------------------------------------------- Export Only --------------------------------------------------->>

function areChangesStored(piiScan: any): boolean {
  if (piiScan === null || piiScan === undefined) {
    piiScan = {}
  }
  let values = false
  if (Object?.keys(piiScan).length > 0) {
    Object?.keys(piiScan).forEach((key) => {
      if (key === FILE_ARRAY) {
        for (const file of piiScan[key].value) {
          if (file?.resultsArray?.length > 0 && !file?.userAnswer) {
            values = true
          }
        }
      } else {
        if (piiScan[key]?.resultsArray?.length > 0 && !piiScan[key]?.userAnswer) {
          values = true
        }
      }
    })
  }
  return values
}

//STEP 1: Scan the File
//Step 2: Validate the file

/*** PII scanning common functions*/

async function getPIIScanInfo(itemPII, piiScan): Promise<PIIScanInfo> {
  let canSubmit = true
  const piiScanResult: any = await validatePII(itemPII, piiScan)
  if (Object.keys(piiScanResult)?.length > 0) {
    for (const key of Object.keys(piiScanResult)) {
      if (key === FILE_ARRAY) {
        for (const file of piiScanResult[key].value) {
          if (file?.resultsArray?.length > 0 && !file.userAnswer) {
            canSubmit = false
          }
        }
      } else if (piiScanResult[key]?.resultsArray?.length > 0 && !piiScanResult[key]?.userAnswer) {
        canSubmit = false
      }
    }
  }

  return { canSubmit, piiScanResult: { ...piiScanResult } }
}

/**<<-------------------------- PII -------------------------->> **/

async function loadPII() {
  // variables
  PII.CategoryFormatList = CATEGORY_PARAMETER_FORMATS_LIST
  PII.CategoryParamsList = CATEGORY_TYPE_PARAMETER_LIST
  // functions
  PII.createPIIConfigTemplate = createPIIConfigTemplate
  PII.getData = getData
  PII.getFormatOrParams = getFormatOrParams
  PII.scanObject_v2 = scanObject_v2
  //   PII.exportToExcel = exportToExcel
}
//PII functions to scan any component
const validatePII = async (
  uploads: {
    FileArray: File[]
  },
  piiScanObj
): Promise<unknown> => {
  return new Promise(function (resolve, reject) {
    const scanFieldObj: any = {}
    // Get values to scan if they are new/modified values and not yet scanned.
    // Loop through each label in the `LABELS_TO_SCAN` array
    for (const label of LABELS_TO_SCAN) {
      if (
        label in uploads &&
        // typeof uploads[label] === 'string' &&
        uploads[label]
      ) {
        // Check if the value is not already scanned for PII
        if (!(piiScanObj && piiScanObj[label] && piiScanObj[label].value === uploads[label])) {
          // Add the value to the `scanFieldObj` object with the label as the key
          scanFieldObj[label] = uploads[label]
        }
      } else {
        // Remove the item from the `piiScanObj` object if it has already been scanned
        if (piiScanObj && piiScanObj[label]) {
          delete piiScanObj[label]
        }
      }
    }
    const filesToUpload = uploads.FileArray ? uploads.FileArray : []
    if (Array.isArray(filesToUpload) && filesToUpload.length > 0) {
      if (
        piiScanObj &&
        piiScanObj.FileArray &&
        Array.isArray(piiScanObj.FileArray.value) &&
        piiScanObj.FileArray.value.length > 0
      ) {
        piiScanObj.FileArray.value.forEach(function (fileObj) {
          fileObj.isStillPresent = false
        })
      }
      // Now fill scanFieldObj.FileArray with only new File Instances
      scanFieldObj.FileArray = filesToUpload.reduce(function (acc, fileInstance) {
        let isNewFileInstance = true
        if (
          piiScanObj &&
          piiScanObj.FileArray &&
          Array.isArray(piiScanObj.FileArray.value) &&
          piiScanObj.FileArray.value.length > 0
        ) {
          // find if this file instance is already scanned
          const fileObj = piiScanObj.FileArray.value.find(function (fileObj) {
            if (fileObj.value === fileInstance) {
              fileObj.isStillPresent = true
              return true
            }
            return false
          })
          if (fileObj) {
            isNewFileInstance = false
          }
        }
        if (isNewFileInstance) {
          acc.push(fileInstance)
        }
        return acc
      }, [])
      // Now check if we have to remove any File Instance from piiScanObj
      if (
        piiScanObj &&
        piiScanObj.FileArray &&
        Array.isArray(piiScanObj.FileArray.value) &&
        piiScanObj.FileArray.value.length > 0
      ) {
        piiScanObj.FileArray.value = piiScanObj.FileArray.value.filter(function (fileObj) {
          const isStillPresent = fileObj.isStillPresent
          delete fileObj.isStillPresent
          return isStillPresent
        })
      }
      // also delete from scanFieldObj if no new File Instances have been found
      if (scanFieldObj.FileArray.length === 0) {
        delete scanFieldObj.FileArray
      }
    } else {
      if (piiScanObj && piiScanObj.FileArray) {
        delete piiScanObj.FileArray
      }
    }
    // show old validations if there is no new data to scan
    if (Object.keys(scanFieldObj).length === 0) {
      resolve(piiScanObj)
    } else {
      PII.scanObject_v2(scanFieldObj).then(function (piiScanInstance) {
        if (piiScanObj) {
          LABELS_TO_SCAN.forEach((label) => {
            if (piiScanInstance[label]) {
              piiScanObj[label] = piiScanInstance[label]
            }
          })
          if (piiScanInstance.FileArray) {
            piiScanObj.FileArray = piiScanInstance.FileArray
          }
        } else {
          piiScanObj = piiScanInstance
        }
        resolve(piiScanObj)
      })
    }
  })
}
const preformBatch = async (
  batchOperation,
  listName,
  piiScan,
  comment,
  userId,
  itemGUID,
  inquiryID,
  labels
) => {
  const batchItems: any = []
  for (const label in piiScan) {
    if (label !== 'FileArray' && labels.includes(label)) {
      const audit = piiScan[label]
      const objectArray = {
        [PIIAuditTrailCols.Comment]: comment,
        [PIIAuditTrailCols.EntityType]: LABELS_TO_SCAN_OBJ[label],
        [PIIAuditTrailCols.FileName]: audit?.value?.name || null,
        [PIIAuditTrailCols.InquiryID]: inquiryID,
        [PIIAuditTrailCols.ItemCreated]: getCurrentUTCDate(),
        [PIIAuditTrailCols.ItemCreatedBy]: userId,
        [PIIAuditTrailCols.ItemGUID]: itemGUID,
        [PIIAuditTrailCols.ItemModified]: getCurrentUTCDate(),
        [PIIAuditTrailCols.ItemModifiedBy]: userId,
        [PIIAuditTrailCols.ItemType]: audit?.value?.type || null,
        [PIIAuditTrailCols.JsonResults]: generatePIIAuditJson(audit),
        [PIIAuditTrailCols.UserAnswer]: audit?.userAnswer || null,
      }
      batchItems.push({
        data: objectArray,
        list: listName,
        op: batchOperation,
      })
    } else {
      const jsonResults = piiScan[label].value

      jsonResults.forEach((result, index) => {
        const objectArray = {
          [PIIAuditTrailCols.Comment]: comment,
          [PIIAuditTrailCols.EntityType]: LABELS_TO_SCAN_OBJ[label],
          // [PIIAuditTrailCols.DocumentPath]: '',
          [PIIAuditTrailCols.FileName]: result.value.name,
          [PIIAuditTrailCols.InquiryID]: inquiryID,
          [PIIAuditTrailCols.ItemCreated]: getCurrentUTCDate(),
          [PIIAuditTrailCols.ItemCreatedBy]: userId,
          [PIIAuditTrailCols.ItemGUID]: itemGUID,
          [PIIAuditTrailCols.ItemModified]: getCurrentUTCDate(),
          [PIIAuditTrailCols.ItemModifiedBy]: userId,
          [PIIAuditTrailCols.ItemType]: result.value.type,
          [PIIAuditTrailCols.JsonResults]: generatePIIAuditJson(result),
          [PIIAuditTrailCols.UserAnswer]: result.userAnswer,
        }

        batchItems.push({
          data: objectArray,
          list: listName,
          op: batchOperation,
        })
      })
    }
  }

  // Example: send the batchItems to a server
  await sendBatchToServer(batchItems)
}

// Example function to simulate sending batch to server
async function sendBatchToServer(batchItems) {
  // Simulate async server request
  await mpfRListsHttp().createBatch(BatchModeTransaction, batchItems).execute()
}

/** handlePIIValidationFailure
 * Handles PII validation failure by updating the display state based on the specified position.
 * Sets the appropriate flags to indicate where the submit button should be shown (top or bottom)
 * and ensures that the PII display is enabled.
 *
 * @param {('bottom' | 'top')} position - The position where the submit button should be shown.
 * @param {Function} setDisplayValidation - The state setter function for updating the display validation state.
 * @returns {Object} The updated failure response object.
 */

const handlePIIValidationFailure = (position: 'bottom' | 'top' | null, setDisplayValidation) => {
  const failureResponse = {
    showBottomSubmit: position === 'bottom',
    showPII: true,
    showTopSubmit: position === 'top',
  }

  setDisplayValidation((prev) => ({
    ...prev,
    ...failureResponse,
  }))
  return failureResponse
}

export {
  LABELS_TO_SCAN_OBJ,
  PII,
  ReturnErrorMessage,
  ShowErrorMessage,
  ToastContainer,
  addToPIIAuditTrail,
  areChangesStored,
  extractTextFromFileBuffer,
  getPIIScanInfo,
  handlePIIValidationFailure,
  loadPII,
  preformBatch,
  shouldAddToPIIAuditTrail,
  toast,
  validatePII
}
